import { useEffect, useState } from "react";
import FileInput from "../../Components/FileInput/FileInput";
import DownloadOrderTemplate from "../../Components/DownloadOrderTemplate";
import Button from "../../Components/Button";
import { createExcelFormData, excelParser } from "../../Utils/file";
import { packetErrorHandler } from "../../Utils/requestUtils";
import Modal from "../../Components/Modal/Modal";
import TabBox, { Tab } from "../../Components/Tabbox/TabBox";
import FilePreviewer from "./FilePreviewer";
import {
  addNLegsBulkOrder,
  uploadNLegsOrderFile,
} from "../../Requests/nLegsRequests";
import {
  addStepBulkOrder,
  uploadStepOrderFileReport,
} from "../../Requests/stepRequests";
import {
  addManualBulkOrder,
  uploadManualOrderFileReport,
} from "../../Requests/manualRequests";
import {
  addCCBulkOrder,
  uploadCCOrderFile,
} from "../../Requests/ccOrder";
import {
  addDeltaHedgeOrderBulkOrder,
  uploadDeltaOrderFile,
} from "../../Requests/deltaHedgeOrder";
import { toast } from "react-toastify";
import CCOrderPreviewTable, { isCCOrderPreviewRowSendable } from "../CCOrder/OrderPreviewTable";
import DeltaHedgeOrderPreviewTable, { isDeltaHedgePreviewRowSendable } from "../DeltaHedgeOrder/OrderPreviewTable";
import ExcelStepOrderPreviewTable from "../../Tables/Order/ExcelStepOrderPreviewTable";
import ExcelNLegOrderPreviewTable from "../../Tables/NLegs/ExcelNLegOrderPreviewTable";

enum ActiveTab {
  NLeg,
  Step,
  Manual,
  Cc,
  DeltaHedge,
}

interface TabWithOrder extends Tab {
  order: number;
}

const UploadAll = () => {
  const [file, setFile] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(ActiveTab.NLeg);
  const [tabChildren, setTabChildren] = useState([] as TabWithOrder[]);

  const onSubmit = () => {
    if (!file) return;
    setLoading(true);
    excelParser(file)
      .then((resp) => {
        const result = [] as TabWithOrder[];
        Object.keys(resp).forEach((key) => {
          if (
            key.startsWith("U2Pair") ||
            key.startsWith("U3Pair") ||
            key.startsWith("U4Pair")
          ) {
            result.push({ index: ActiveTab.NLeg, title: "NLeg", order: 6 });
          } else if (
            key.startsWith("ShekarStepT") ||
            key.startsWith("ShekarStepM") ||
            key.startsWith("KaminStepM") ||
            key.startsWith("KaminStepT") ||
            key.startsWith("CompetitionStepM") ||
            key.startsWith("CompetitionStepT")
          ) {
            result.push({ index: ActiveTab.Step, title: "Step", order: 2 });
          } else if (
            key.startsWith("ManualStepM") ||
            key.startsWith("ManualStepT")
          ) {
            result.push({ index: ActiveTab.Manual, title: "Manual", order: 3 });
          } else if (key.startsWith("CC")) {
            result.push({ index: ActiveTab.Cc, title: "CoveredCall", order: 4 });
          } else if (key.startsWith("DH")) {
            result.push({
              index: ActiveTab.DeltaHedge,
              title: "DeltaHedge",
              order: 5,
            });
          }
        });
        if (!result || !result.length) return toast.error("خطا در خواندن فایل");
        const finalResult = Object.values(
          result.reduce(
            (acc, cur) => Object.assign(acc, { [cur.index]: cur }),
            {}
          )
        );
        setTabChildren(
          finalResult.sort((a: any, b: any) => a.order - b.order) as any
        );
        setActiveTab((finalResult as any)[0].index);
        setIsOpen(true);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getAddBulkOrderRequest = () => {
    switch (activeTab) {
      case ActiveTab.NLeg:
        return addNLegsBulkOrder;

      case ActiveTab.Step:
        return addStepBulkOrder;
      case ActiveTab.Manual:
        return addManualBulkOrder;
      case ActiveTab.Cc:
        return addCCBulkOrder;
      case ActiveTab.DeltaHedge:
        return addDeltaHedgeOrderBulkOrder;
    }
  };
  const getFileRequest = () => {
    switch (activeTab) {
      case ActiveTab.NLeg:
        return () => uploadNLegsOrderFile(file);

      case ActiveTab.Step:
        return () => uploadStepOrderFileReport(file);
      case ActiveTab.Manual:
        return () => uploadManualOrderFileReport(file);
      case ActiveTab.Cc:
        return () => uploadCCOrderFile(file);
      case ActiveTab.DeltaHedge:
        return () => uploadDeltaOrderFile(file);
    }
  };

  const getUserIds = (bulkOrderRows: any[] | null) => {
    if (!bulkOrderRows) return [];
    const res: number[] = [];
    if (activeTab === ActiveTab.Step || activeTab === ActiveTab.Manual)
      bulkOrderRows.forEach((row) => {
        res.push(row.sahraUserId);
      });
    if (activeTab === ActiveTab.Cc || activeTab === ActiveTab.DeltaHedge)
      bulkOrderRows.forEach((row) => {
        res.push(row.accountId);
      });

    return res;
  };

  const modalUI = (
    <div className="w-full flex flex-col justify-start items-center gap-5">
      <TabBox
        children={tabChildren}
        activeTab={activeTab}
        onChange={(_, i) => setActiveTab(i)}
      />

      <FilePreviewer
        key={activeTab}
        bulkOrderRequest={getAddBulkOrderRequest()}
        fileRequest={getFileRequest()}
        onClose={() => setIsOpen(false)}
        getUserIds={getUserIds}
        isRowSendable={activeTab === ActiveTab.Cc ? isCCOrderPreviewRowSendable : activeTab === ActiveTab.DeltaHedge ? isDeltaHedgePreviewRowSendable : undefined}
        renderPreview={activeTab === ActiveTab.Cc
          ? (report) => <CCOrderPreviewTable values={report.report.map((row) => ({ ...row.orderRow, accessMessage: row.accessMessage || row.orderRow?.accessMessage }))} />
          : activeTab === ActiveTab.DeltaHedge
            ? (report) => <DeltaHedgeOrderPreviewTable values={report.report.map((row) => ({ ...row.orderRow, accessMessage: row.accessMessage || row.orderRow?.accessMessage }))} />
            : activeTab === ActiveTab.Manual || activeTab === ActiveTab.Step
              ? (report) => <ExcelStepOrderPreviewTable reports={report} />
              : activeTab === ActiveTab.NLeg
                ? (report) => <ExcelNLegOrderPreviewTable reports={report} />
                : undefined}
      />
    </div>
  );

  return (
    <div className="w-full flex flex-col justify-center items-center gap-10">
      {isOpen ? <Modal title="آپلود گروهی" modalBoxClassName={activeTab === ActiveTab.Cc || activeTab === ActiveTab.DeltaHedge ? "!min-w-0 w-[1320px]" : undefined} onClose={() => setIsOpen(false)} UI={modalUI} /> : <></>}
      <div className="font-bold">آپلود گروهی</div>
      <div className="flex flex-col items-center gap-3">
        <FileInput
          UI={<></>}
          label="انتخاب فایل"
          name="formFile"
          onChange={(v) => setFile(v)}
          value={file}
          fileExtension=".xlsx, .xls"
          className="!w-60 h-32 text-[16px]"
        />
        <DownloadOrderTemplate kind="all" />
      </div>
      <div className="w-60">
        <Button
          onClick={onSubmit}
          text="ثبت"
          variant="contained"
          disabled={!file}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default UploadAll;
